import React, { useState, useMemo, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  DatePicker, 
  Space, 
  Typography,
  Statistic,
  Progress,
  Table,
  Tag,
  Select,
  Empty,
  Spin,
  message,
  Switch
} from 'antd';
import { 
  UserOutlined, 
  ClockCircleOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  TrophyOutlined,
  CalendarOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { Gantt, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { useQuery } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;
const { Option } = Select;

/**
 * 人员统计页面组件
 * 根据时间段统计指定人员的工作量和任务列表
 */
const PersonalStats = () => {
  const { user } = useAuthStore();
  const [selectedUserId, setSelectedUserId] = useState(user?.id); // 默认选中当前用户
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'), // 默认最近30天
    dayjs()
  ]);
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [viewMode, setViewMode] = useState('list'); // 'list' 或 'gantt'

  // 检查用户权限，是否可以查看其他用户数据
  const canViewOtherUsers = ['admin', 'manager', 'pm'].includes(user?.role);

  // 获取用户列表（仅管理员、主管、项目经理可见）
  const { data: usersData } = useQuery(
    'allUsers',
    () => axios.get('/auth/users').then(res => res.data.data),
    { 
      enabled: canViewOtherUsers,
      staleTime: 5 * 60 * 1000 // 5分钟缓存
    }
  );

  /**
   * 函数：buildDesignerList 构建可循环的设计师ID列表
   * 说明：仅在具备查看其他用户权限时，基于接口返回的用户列表筛选role为designer，返回排序后的id数组
   */
  const buildDesignerList = useMemo(() => {
    if (!canViewOtherUsers) return [];
    const list = usersData?.users || [];
    const designers = list.filter(u => u.role === 'designer');
    designers.sort((a, b) => (a.realName || '').localeCompare(b.realName || ''));
    return designers.map(d => d.id);
  }, [usersData, canViewOtherUsers]);

  // Shift键按下状态与选择动画状态
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [animateSelect, setAnimateSelect] = useState(false);

  /**
   * 函数：applySelectionAnimation 触发人员选择组件的平滑动画效果
   * 说明：在切换选中人员时短暂放大与淡入，提升视觉反馈
   */
  const applySelectionAnimation = () => {
    setAnimateSelect(true);
    setTimeout(() => setAnimateSelect(false), 220);
  };

  /**
   * 函数：handleShiftKey 监听Shift键按下与释放
   * 说明：按下Shift时开启滚轮切换模式；释放后停止响应滚轮切换
   */
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Shift') setIsShiftPressed(true);
    };
    const onKeyUp = (e) => {
      if (e.key === 'Shift') setIsShiftPressed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  /**
   * 函数：handleWheelSwitchDesigner 在按住Shift时根据滚轮方向切换选中设计师
   * 说明：向上滚动切换到上一个、向下滚动到下一个；支持首尾循环，不阻止页面正常滚动
   */
  useEffect(() => {
    const onWheel = (e) => {
      if (!isShiftPressed) return;
      if (!buildDesignerList?.length) return;
      const total = buildDesignerList.length;
      const currentIdx = buildDesignerList.indexOf(selectedUserId);
      const baseIdx = currentIdx >= 0 ? currentIdx : (e.deltaY < 0 ? total - 1 : 0);
      const nextIdx = (baseIdx + (e.deltaY > 0 ? 1 : -1) + total) % total;
      const nextId = buildDesignerList[nextIdx];
      if (nextId && nextId !== selectedUserId) {
        setSelectedUserId(nextId);
        applySelectionAnimation();
      }
      // 不调用preventDefault，保证页面可正常滚动
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
    };
  }, [isShiftPressed, buildDesignerList, selectedUserId]);

  /**
   * 项目颜色映射函数
   * @param {string} projectName - 项目名称
   * @returns {string} 颜色值
   */
  const getProjectColor = (projectName) => {
    const colors = [
      '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
      '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'
    ];
    // 根据项目名称生成一致的颜色索引
    let hash = 0;
    for (let i = 0; i < projectName.length; i++) {
      hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  /**
   * 函数：darkenColor 将十六进制颜色按比例加深
   * 参数：hex - 颜色字符串（如 #1890ff）；ratio - 加深比例(0-1)
   * 返回：加深后的颜色字符串
   */
  const darkenColor = (hex, ratio = 0.35) => {
    try {
      const h = hex.replace('#', '');
      const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      const dr = Math.max(0, Math.min(255, Math.round(r * (1 - ratio))));
      const dg = Math.max(0, Math.min(255, Math.round(g * (1 - ratio))));
      const db = Math.max(0, Math.min(255, Math.round(b * (1 - ratio))));
      const toHex = (n) => n.toString(16).padStart(2, '0');
      return `#${toHex(dr)}${toHex(dg)}${toHex(db)}`;
    } catch (_) {
      return hex;
    }
  };

  /**
   * 处理视图模式切换
   * @param {boolean} checked - 是否为甘特图模式
   */
  const handleViewModeChange = (checked) => {
    setViewMode(checked ? 'gantt' : 'list');
  };


  // 获取选中用户的信息
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    if (selectedUserId === user?.id) return user;
    return usersData?.users?.find(u => u.id === selectedUserId) || null;
  }, [selectedUserId, user, usersData]);

  // 获取指定用户的工作日志数据
  const { data: logsData, isLoading: logsLoading } = useQuery(
    ['userLogs', selectedUserId, dateRange],
    () => {
      const params = {
        limit: 1000 // 获取足够多的数据用于统计
      };
      
      // 如果选择的不是当前用户，且有权限查看其他用户数据，则添加userId参数
      if (selectedUserId && selectedUserId !== user?.id && canViewOtherUsers) {
        params.userId = selectedUserId;
      }
      
      if (dateRange.length === 2) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      
      return axios.get('/daily-logs', { params }).then(res => res.data.data);
    },
    { 
      refetchInterval: 30000,
      enabled: !!dateRange.length && !!selectedUserId
    }
  );

  // 获取指定用户的任务数据
  const { data: tasksData, isLoading: tasksLoading } = useQuery(
    ['userTasks', selectedUserId],
    () => {
      // 如果选择的是当前用户，使用/tasks/my接口
      if (selectedUserId === user?.id) {
        return axios.get('/tasks/my').then(res => res.data.data);
      }
      
      // 如果选择的是其他用户，且有权限，使用/tasks接口并传递assignedTo参数
      if (canViewOtherUsers && selectedUserId) {
        return axios.get('/tasks', { 
          params: { assignedTo: selectedUserId, limit: 1000 } 
        }).then(res => {
          // 将数据格式转换为与/tasks/my相同的格式
          const tasks = res.data.data.tasks || [];
          const tasksByProject = {};
          
          tasks.forEach(task => {
            const projectId = task.project?.id;
            if (!tasksByProject[projectId]) {
              tasksByProject[projectId] = {
                project: task.project,
                tasks: []
              };
            }
            tasksByProject[projectId].tasks.push(task);
          });
          
          return { tasksByProject: Object.values(tasksByProject) };
        });
      }
      
      return Promise.resolve({ tasksByProject: [] });
    },
    { 
      refetchInterval: 30000,
      enabled: !!selectedUserId
    }
  );

  /**
   * 处理用户选择变化
   * @param {number} userId - 选中的用户ID
   */
  const handleUserChange = (userId) => {
    if (!canViewOtherUsers && userId !== user?.id) {
      message.warning('您没有权限查看其他用户的数据');
      return;
    }
    setSelectedUserId(userId);
  };

  /**
   * 处理日期范围变化
   * @param {Array} dates - 日期范围数组
   */
  const handleDateRangeChange = (dates) => {
    setDateRange(dates || []);
  };

  /**
   * 计算工作量统计数据
   */
  const workStats = useMemo(() => {
    if (!logsData?.logs) {
      return {
        totalWorkHours: 0,
        totalLogs: 0,
        completedTasks: 0,
        avgDailyHours: 0,
        workEfficiency: 0
      };
    }

    const logs = logsData.logs;
    
    // 安全地计算总工作时长，确保数值有效
    const totalWorkHours = logs.reduce((sum, log) => {
      const workHours = parseFloat(log.workHours);
      return sum + (isNaN(workHours) ? 0 : workHours);
    }, 0);
    
    const totalLogs = logs.length;
    
    // 计算完成的任务数量（进度达到100%的任务）
    const completedTaskIds = new Set();
    logs.forEach(log => {
      const progressAfter = parseFloat(log.progressAfter);
      if (!isNaN(progressAfter) && progressAfter === 100) {
        completedTaskIds.add(log.taskId);
      }
    });
    const completedTasks = completedTaskIds.size;

    // 计算平均每日工作时长（按日志数计算）
    const avgDailyHours = totalLogs > 0 && !isNaN(totalWorkHours) ? totalWorkHours / totalLogs : 0;

    // 计算工作效率（基于进度完成情况）
    const totalProgressIncrease = logs.reduce((sum, log) => {
      const progressBefore = parseFloat(log.progressBefore) || 0;
      const progressAfter = parseFloat(log.progressAfter) || 0;
      const increase = progressAfter - progressBefore;
      return sum + (isNaN(increase) ? 0 : increase);
    }, 0);
    const workEfficiency = totalLogs > 0 && !isNaN(totalProgressIncrease) ? (totalProgressIncrease / totalLogs) : 0;

    // 确保所有返回值都是有效数字
    return {
      totalWorkHours: isNaN(totalWorkHours) ? 0 : Math.round(totalWorkHours * 10) / 10,
      totalLogs,
      completedTasks,
      avgDailyHours: isNaN(avgDailyHours) ? 0 : Math.round(avgDailyHours * 10) / 10,
      workEfficiency: isNaN(workEfficiency) ? 0 : Math.round(workEfficiency * 10) / 10
    };
  }, [logsData, dateRange]);

  /**
   * 获取任务列表数据（包含工作时长统计）
   */
  const taskListData = useMemo(() => {
    if (!tasksData?.tasksByProject || !logsData?.logs) {
      return [];
    }

    const logs = logsData.logs;
    const allTasks = [];

    // 展平任务数据
    tasksData.tasksByProject.forEach(group => {
      group.tasks.forEach(task => {
        // 计算该任务在时间段内的工作时长
        const taskLogs = logs.filter(log => log.taskId === task.id);
        const workHours = taskLogs.reduce((sum, log) => {
          const logWorkHours = parseFloat(log.workHours);
          return sum + (isNaN(logWorkHours) ? 0 : logWorkHours);
        }, 0);
        const logCount = taskLogs.length;
        
        // 获取最新日志日期
        let latestLogDate = null;
        if (taskLogs.length > 0) {
          const sortedLogs = taskLogs.sort((a, b) => dayjs(b.logDate).unix() - dayjs(a.logDate).unix());
          latestLogDate = sortedLogs[0].logDate;
        }

        allTasks.push({
          ...task,
          projectName: group.project.name,
          workHours: isNaN(workHours) ? 0 : Math.round(workHours * 10) / 10,
          logCount,
          latestLogDate
        });
      });
    });

    // 应用筛选条件
    let filteredTasks = allTasks;
    
    if (statusFilter) {
      filteredTasks = filteredTasks.filter(task => task.status === statusFilter);
    }

    return filteredTasks;
  }, [tasksData, logsData, statusFilter]);

  /**
   * 将日志数据转换为甘特图格式
   */
  const ganttData = useMemo(() => {
    if (!logsData?.logs || !tasksData?.tasksByProject) {
      return [];
    }

    const logs = logsData.logs;
    const taskMap = new Map();
    
    // 构建任务映射
    tasksData.tasksByProject.forEach(group => {
      group.tasks.forEach(task => {
        taskMap.set(task.id, {
          ...task,
          projectName: group.project.name,
          projectColor: getProjectColor(group.project.name)
        });
      });
    });

    // 按任务分组日志
    const taskLogs = {};
    logs.forEach(log => {
      if (!taskLogs[log.taskId]) {
        taskLogs[log.taskId] = [];
      }
      taskLogs[log.taskId].push(log);
    });

    // 转换为甘特图数据
    const ganttTasks = [];
    Object.entries(taskLogs).forEach(([taskId, taskLogList]) => {
      const task = taskMap.get(parseInt(taskId));
      if (!task) return;

      // 按日期排序日志
      const sortedLogs = taskLogList.sort((a, b) => 
        new Date(a.logDate) - new Date(b.logDate)
      );

      if (sortedLogs.length === 0) return;

      // 计算任务的开始和结束日期
      const startDate = new Date(sortedLogs[0].logDate);
      const endDate = new Date(sortedLogs[sortedLogs.length - 1].logDate);
      
      // 如果开始日期和结束日期相同，结束日期加一天
      if (startDate.getTime() === endDate.getTime()) {
        endDate.setDate(endDate.getDate() + 1);
      }

      // 计算总工作时长
      const totalHours = sortedLogs.reduce((sum, log) => sum + (log.workHours || 0), 0);

      ganttTasks.push({
        start: startDate,
        end: endDate,
        name: task.title,  // 只显示任务名称
        id: `task-${taskId}`,
        progress: task.progress || 0,
        type: 'task',
        project: task.projectName,
        dependencies: [],
        styles: {
          backgroundColor: task.projectColor,
          backgroundSelectedColor: task.projectColor,
          // 将进度填充颜色加深以提升对比度
          progressColor: darkenColor(task.projectColor, 0.35),
          progressSelectedColor: darkenColor(task.projectColor, 0.35)
        },
        // 自定义数据
        taskData: {
          ...task,
          totalHours: Math.round(totalHours * 10) / 10,
          logCount: sortedLogs.length,
          logs: sortedLogs
        }
      });
    });

    // 按项目名称排序
    return ganttTasks.sort((a, b) => {
      if (a.project !== b.project) {
        return a.project.localeCompare(b.project);
      }
      return a.name.localeCompare(b.name);
    });
  }, [logsData, tasksData, getProjectColor]);

  /**
   * 获取状态标签颜色
   * @param {string} status - 任务状态
   * @returns {string} 颜色值
   */
  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      in_progress: 'processing',
      completed: 'success',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  /**
   * 获取状态文本
   * @param {string} status - 任务状态
   * @returns {string} 状态文本
   */
  const getStatusText = (status) => {
    const texts = {
      pending: '待开始',
      in_progress: '进行中',
      completed: '已完成',
      cancelled: '已取消'
    };
    return texts[status] || status;
  };

  /**
   * 格式化日志日期显示（紧凑格式）
   * @param {string} date - 日期字符串
   * @returns {JSX.Element} 格式化的日期显示
   */
  const formatLogDate = (date) => {
    if (!date) {
      return <Text type="secondary" style={{ fontSize: '12px' }}>无</Text>;
    }
    
    const logDate = dayjs(date);
    const today = dayjs();
    const yesterday = today.subtract(1, 'day');
    
    let displayText;
    if (logDate.isSame(today, 'day')) {
      displayText = '今天';
    } else if (logDate.isSame(yesterday, 'day')) {
      displayText = '昨天';
    } else {
      // 使用更紧凑的格式：M/D（如：3/15）
      displayText = logDate.format('M/D');
    }
    
    return (
      <Space size={4}>
        <CalendarOutlined style={{ fontSize: '12px' }} />
        <Text style={{ fontSize: '12px' }}>{displayText}</Text>
      </Space>
    );
  };

  /**
   * 格式化甘特图中的简短日期显示
   * @param {Date} date - 日期对象
   * @returns {string} 简短日期格式（如：9/3）
   */
  const formatShortDate = (date) => {
    if (!date) return '';
    const d = dayjs(date);
    return d.format('M/D');
  };

  /**
   * 格式化甘特图中的周几显示
   * @param {Date} date - 日期对象
   * @returns {string} 周几格式（如：周三）
   */
  const formatWeekday = (date) => {
    if (!date) return '';
    const d = dayjs(date);
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return weekdays[d.day()];
  };

  /**
   * 设置甘特图时间轴双行显示
   */
  useEffect(() => {
    if (viewMode === 'gantt' && ganttData.length > 0) {
      // 延迟执行以确保DOM已渲染
      const timer = setTimeout(() => {
        const headerItems = document.querySelectorAll('.gantt .gantt-header .gantt-header-item');
        headerItems.forEach((item) => {
          const textElement = item.querySelector('.gantt-header-item-text');
          if (textElement && textElement.textContent) {
            // 解析日期文本
            const dateText = textElement.textContent.trim();
            try {
              // 尝试解析日期
              const date = new Date(dateText);
              if (!isNaN(date.getTime())) {
                const shortDate = formatShortDate(date);
                const weekday = formatWeekday(date);
                
                // 设置data属性用于CSS伪元素显示
                textElement.setAttribute('data-date', shortDate);
                textElement.setAttribute('data-weekday', weekday);
                
                // 清空原始文本内容
                textElement.textContent = '';
              }
            } catch (error) {
              console.log('日期解析失败:', dateText);
            }
          }
        });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [viewMode, ganttData, formatShortDate, formatWeekday]);

  // 任务列表表格列定义
  /**
   * 列表列定义调整：
   * - 将“项目名称”合并到“任务名称”列的下方显示（蓝色小字，参照甘特图模式），避免重复列。
   * - 移除单独的“项目名称”列，提升信息密度与可读性。
   */
  const taskColumns = [
    {
      title: '任务名称',
      dataIndex: 'title',
      key: 'title',
      width: 260,
      render: (_, record) => (
        <div style={{ lineHeight: 1.2 }}>
          <Text strong>{record.title}</Text>
          {record.projectName && (
            <div style={{ color: '#1890ff', fontSize: 12, marginTop: 2 }}>
              [{record.projectName}]
            </div>
          )}
        </div>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      filters: [
        { text: '待开始', value: 'pending' },
        { text: '进行中', value: 'in_progress' },
        { text: '已完成', value: 'completed' },
        { text: '已取消', value: 'cancelled' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: '最新日志',
      dataIndex: 'latestLogDate',
      key: 'latestLogDate',
      width: 80,
      render: (date) => formatLogDate(date),
      sorter: (a, b) => {
        // 没有日志的任务排在最后
        if (!a.latestLogDate && !b.latestLogDate) return 0;
        if (!a.latestLogDate) return 1;
        if (!b.latestLogDate) return -1;
        return dayjs(b.latestLogDate).unix() - dayjs(a.latestLogDate).unix();
      }
    },
    {
      title: '进度',
      dataIndex: 'progress',
      key: 'progress',
      width: 120,
      render: (progress) => (
        <Progress 
          percent={progress} 
          size="small"
          status={progress === 100 ? 'success' : 'active'}
        />
      ),
      sorter: (a, b) => a.progress - b.progress
    },
    {
      title: '工作时长',
      dataIndex: 'workHours',
      key: 'workHours',
      width: 100,
      render: (hours) => {
        // 确保工作时长显示为有效数字，避免NaN显示
        const validHours = isNaN(hours) || hours === null || hours === undefined ? 0 : hours;
        return (
          <Space>
            <ClockCircleOutlined />
            <Text>{validHours}h</Text>
          </Space>
        );
      },
      sorter: (a, b) => {
        const aHours = isNaN(a.workHours) ? 0 : a.workHours;
        const bHours = isNaN(b.workHours) ? 0 : b.workHours;
        return aHours - bHours;
      }
    },
    {
      title: '日志数量',
      dataIndex: 'logCount',
      key: 'logCount',
      width: 100,
      render: (count) => (
        <Space>
          <FileTextOutlined />
          <Text>{count}条</Text>
        </Space>
      ),
      sorter: (a, b) => a.logCount - b.logCount
    }
  ];

  const isLoading = logsLoading || tasksLoading;

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <TeamOutlined />
            任务统计
            {selectedUser && (
              <Text type="secondary">- {selectedUser.realName}</Text>
            )}
          </Space>
        }
        className="status-card"
      >
        {/* 用户选择和时间范围选择器 */}
        <div style={{ marginBottom: '24px' }}>
          <Space wrap size="large">
            {canViewOtherUsers && (
              <div>
                <Text strong style={{ marginRight: 8 }}>选择人员：</Text>
                <Select
                  placeholder="请选择人员"
                  style={{ 
                    width: 200,
                    transition: 'transform 0.22s ease, box-shadow 0.22s ease, opacity 0.22s ease',
                    transform: animateSelect ? 'scale(1.02)' : 'none',
                    boxShadow: animateSelect ? '0 0 0 2px rgba(24,144,255,0.35)' : 'none',
                    opacity: animateSelect ? 0.95 : 1
                  }}
                  value={selectedUserId}
                  onChange={handleUserChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {/* 当前用户选项 */}
                  <Option key={user?.id} value={user?.id}>
                    {user?.realName} (我)
                  </Option>
                  {/* 其他用户选项 */}
                  {usersData?.users?.filter(u => u.id !== user?.id).map(u => (
                    <Option key={u.id} value={u.id}>
                      {u.realName} - {u.position}
                    </Option>
                  ))}
                </Select>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>按住 Shift + 滚轮 可在设计师间循环切换</Text>
              </div>
            )}
            <div>
              <Text strong style={{ marginRight: 8 }}>统计时间段：</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                placeholder={['开始日期', '结束日期']}
              />
            </div>
          </Space>
        </div>

        {/* 工作量统计卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small">
              <Statistic
                title={`总工作时长${selectedUser ? ` (${selectedUser.realName})` : ''}`}
                value={workStats.totalWorkHours}
                suffix="小时"
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small">
              <Statistic
                title="日志记录数"
                value={workStats.totalLogs}
                suffix="条"
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small">
              <Statistic
                title="完成任务数"
                value={workStats.completedTasks}
                suffix="个"
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small">
              <Statistic
                title="平均日工时"
                value={workStats.avgDailyHours}
                suffix="小时"
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Card size="small">
              <Statistic
                title="工作效率"
                value={workStats.workEfficiency}
                suffix="%/次"
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#eb2f96' }}
              />
            </Card>
          </Col>
        </Row>

        {/* 任务列表 */}
        <Card 
          title="任务列表"
          size="small"
          extra={
            <Space>
              {/* 甘特图/列表切换开关 */}
              <Space>
                <UnorderedListOutlined style={{ color: viewMode === 'list' ? '#1890ff' : '#8c8c8c' }} />
                <Switch 
                  checked={viewMode === 'gantt'}
                  onChange={handleViewModeChange}
                  size="small"
                />
                <BarChartOutlined style={{ color: viewMode === 'gantt' ? '#1890ff' : '#8c8c8c' }} />
              </Space>
              
              <Select
                  placeholder="选择状态"
                  allowClear
                  style={{ width: 120 }}
                  value={statusFilter}
                  onChange={setStatusFilter}
                >
                  <Option value="pending">待开始</Option>
                  <Option value="in_progress">进行中</Option>
                  <Option value="completed">已完成</Option>
                  <Option value="cancelled">已取消</Option>
                </Select>
            </Space>
          }
        >
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : viewMode === 'list' ? (
            // 列表视图
            taskListData.length === 0 ? (
              <Empty 
                description="暂无任务数据"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Table
                columns={taskColumns}
                dataSource={taskListData}
                rowKey="id"
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                  defaultPageSize: 10,
                  pageSizeOptions: ['10', '20', '50']
                }}
                scroll={{ x: 1000 }}
                size="small"
              />
            )
          ) : (
            // 甘特图视图
            ganttData.length === 0 ? (
              <Empty 
                description="暂无日志数据"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div style={{ height: '400px', overflow: 'auto' }}>
                <div style={{ position: 'relative' }}>
                  <Gantt
                    tasks={ganttData}
                    viewMode={ViewMode.Day}
                    locale="zh-CN"
                    listCellWidth="200px"
                    columnWidth={35}  // 减小列宽，使时间轴更紧凑
                    rowHeight={65}  // 增加行高以容纳任务名称、项目名称和日期三行内容
                    barCornerRadius={3}
                    handleWidth={8}
                    fontFamily="Arial, sans-serif"
                    fontSize="10px"  // 进一步减小字体大小以适应双行显示
                    barFill={60}
                    rtl={false}
                    headerHeight={60}  // 增加时间轴高度以容纳双行显示
                    onDoubleClick={(task) => {
                      console.log('双击任务:', task);
                    }}
                    // 自定义时间轴渲染
                     TaskListHeader={({ headerHeight }) => (
                       <div style={{
                         height: headerHeight,
                         backgroundColor: '#fafafa',
                         border: '1px solid #d9d9d9',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         fontSize: '12px',
                         fontWeight: 'bold'
                       }}>
                         任务列表
                       </div>
                     )}
                     // 自定义时间轴头部渲染
                     TaskListTable={({ rowHeight, rowWidth, fontFamily, fontSize, locale, tasks, selectedTaskId, setSelectedTask, onExpanderClick }) => (
                       <div style={{ width: rowWidth }}>
                         {tasks.map((task, index) => (
                           <div
                             key={task.id}
                             style={{
                               height: rowHeight,
                               backgroundColor: selectedTaskId === task.id ? '#e6f7ff' : index % 2 === 0 ? '#fafafa' : '#ffffff',
                               border: '1px solid #e8e8e8',
                               display: 'flex',
                               alignItems: 'center',
                               padding: '0 8px',
                               fontSize: fontSize,
                               fontFamily: fontFamily,
                               cursor: 'pointer',
                               borderBottom: '1px solid #e8e8e8'
                             }}
                             onClick={() => setSelectedTask && setSelectedTask(task.id)}
                           >
                             <div style={{ 
                               overflow: 'hidden', 
                               textOverflow: 'ellipsis', 
                               whiteSpace: 'nowrap',
                               fontSize: '11px',
                               width: '100%'
                             }}>
                               {/* 任务名称 */}
                               <div style={{ 
                                 fontWeight: 'bold', 
                                 marginBottom: '1px',
                                 fontSize: '11px',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap'
                               }}>
                                 {task.name}
                               </div>
                               {/* 项目名称 */}
                               <div style={{ 
                                 color: '#1890ff', 
                                 fontSize: '9px',
                                 marginBottom: '1px',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap'
                               }}>
                                 [{task.project}]
                               </div>
                               {/* 日期范围 */}
                               <div style={{ 
                                 color: '#666', 
                                 fontSize: '9px',
                                 overflow: 'hidden',
                                 textOverflow: 'ellipsis',
                                 whiteSpace: 'nowrap'
                               }}>
                                 {formatShortDate(task.start)} ~ {formatShortDate(task.end)}
                               </div>
                             </div>
                           </div>
                         ))}
                       </div>
                     )}
                    TooltipContent={({ task, fontSize, fontFamily }) => {
                      const taskData = task.taskData;
                      return (
                        <div style={{
                          fontSize,
                          fontFamily,
                          padding: '8px',
                          backgroundColor: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '4px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                          <div><strong>{task.name}</strong></div>
                           <div>项目: {task.project}</div>
                           <div>进度: {task.progress}%</div>
                           <div>工作时长: {taskData?.totalHours || 0}小时</div>
                           <div>日志数量: {taskData?.logCount || 0}条</div>
                           <div>时间段: {formatShortDate(task.start)} ~ {formatShortDate(task.end)}</div>
                           <div>周期: {formatWeekday(task.start)} 至 {formatWeekday(task.end)}</div>
                        </div>
                      );
                    }}
                  />
                  {/* 添加全局样式来优化甘特图显示 */}
                    <style dangerouslySetInnerHTML={{
                      __html: `
                        /* 优化甘特图时间轴显示 */
                        .gantt .gantt-header {
                          height: 60px !important;
                        }
                        .gantt .gantt-header .gantt-header-item {
                          height: 60px !important;
                          font-size: 9px !important;
                          line-height: 1.2 !important;
                          padding: 4px 2px !important;
                          text-align: center !important;
                          border-right: 1px solid #e8e8e8 !important;
                          background: #fafafa !important;
                          position: relative !important;
                        }
                        .gantt .gantt-header .gantt-header-item .gantt-header-item-text {
                           display: flex !important;
                           flex-direction: column !important;
                           justify-content: center !important;
                           align-items: center !important;
                           height: 100% !important;
                           gap: 2px !important;
                           position: relative !important;
                         }
                         .gantt .gantt-header .gantt-header-item .gantt-header-item-text::before {
                           content: attr(data-date) !important;
                           font-weight: bold !important;
                           font-size: 10px !important;
                           line-height: 1 !important;
                           display: block !important;
                         }
                         .gantt .gantt-header .gantt-header-item .gantt-header-item-text::after {
                           content: attr(data-weekday) !important;
                           font-size: 8px !important;
                           color: #666 !important;
                           line-height: 1 !important;
                           display: block !important;
                         }
                        /* 优化甘特图任务条显示 */
                        .gantt .gantt-task {
                          border-radius: 3px !important;
                          height: 40px !important;  /* 调整任务条高度 */
                          margin: 12px 0 !important;  /* 垂直居中 */
                        }
                        .gantt .gantt-task-content {
                          font-size: 10px !important;
                          padding: 4px 6px !important;
                          line-height: 1.2 !important;
                          display: flex !important;
                          flex-direction: column !important;
                          justify-content: center !important;
                          height: 100% !important;
                          white-space: pre-line !important;  /* 支持换行符显示 */
                          overflow: hidden !important;
                          text-overflow: ellipsis !important;
                        }
                        /* 任务条内的项目名称样式 */
                        .gantt .gantt-task-content .task-project {
                          font-size: 8px !important;
                          color: rgba(255, 255, 255, 0.8) !important;
                          margin-top: 1px !important;
                        }
                        /* 优化任务列表显示 */
                        .gantt .gantt-task-list-wrapper {
                          border-right: 1px solid #e8e8e8 !important;
                        }
                        .gantt .gantt-task-list-header {
                          background: #fafafa !important;
                          border-bottom: 1px solid #e8e8e8 !important;
                        }
                      `
                    }} />
                </div>
              </div>
            )
          )}
        </Card>
      </Card>
    </div>
  );
};

export default PersonalStats;